/**
 * Media helpers (V1 auth-profiles-gdpr) — multipart uploads + authed image
 * rendering. The typed client (lib/api.ts) is JSON-only; uploads mirror its
 * behaviors (bearer, single 401 refresh-retry, error envelope) over FormData.
 */
import { API_BASE, ApiError, refreshSession } from "../../lib/api.js";
import { sessionStore } from "../../lib/store.js";

export interface MediaItemWire {
  id: string;
  kind: string;
  status: string;
  position?: number | null;
}

export async function uploadMultipart<T = MediaItemWire>(
  path: string,
  file: File | Blob,
  fields: Record<string, string> = {},
  retried = false,
): Promise<T> {
  const token = sessionStore.get().accessToken;
  const form = new FormData();
  // field parts FIRST — the server's readMultipart collects fields before the file
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append("file", file);
  const res = await fetch(API_BASE + path, {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: "include",
  });
  if (res.status === 401 && !retried && token) {
    const ok = await refreshSession();
    if (ok) return uploadMultipart<T>(path, file, fields, true);
  }
  if (!res.ok) {
    let envelope: ConstructorParameters<typeof ApiError>[1] = {};
    try {
      envelope = (await res.json()) as typeof envelope;
    } catch {
      envelope = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new ApiError(res.status, envelope);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ---------------------------------------------------------------------------
// Authed media fetch → object URL (the media routes are bearer-gated; a plain
// <img src> carries no Authorization header). Small in-memory cache; object
// URLs live for the session (media ids are immutable content).
// ---------------------------------------------------------------------------
const objectUrls = new Map<string, Promise<string | null>>();

export function authedMediaObjectUrl(mediaId: string): Promise<string | null> {
  let cached = objectUrls.get(mediaId);
  if (!cached) {
    cached = (async () => {
      try {
        const token = sessionStore.get().accessToken;
        const res = await fetch(`${API_BASE}/media/${mediaId}`, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
          credentials: "include",
          redirect: "follow", // 302 → signed /raw URL (signature-auth'd)
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    })();
    objectUrls.set(mediaId, cached);
  }
  return cached;
}

/** Drop a cached object URL (after delete/replace). */
export function evictMediaObjectUrl(mediaId: string): void {
  const cached = objectUrls.get(mediaId);
  objectUrls.delete(mediaId);
  void cached?.then((url) => {
    if (url) URL.revokeObjectURL(url);
  });
}
